// Detalhe do caso, aberto pelo clique no card. Portado do protótipo — ADR-0005.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, CheckCheck, ChevronDown, Eye, Scale, Send } from 'lucide-react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { Aviso, BarraDaTrilha, Operadora, PontoDaTrilha, Tag } from '../../../shared/ui/primitivas';
import { variaveisDaMarca } from '../../../tema/variaveisDaMarca';
import type { AneryCard, Trilha } from '../../../shared/ui/tiposDaEsteira';
import {
  CAMPOS,
  MOMENTO_ESTADO,
  ORDEM_DAS_TRILHAS,
  ROTULO_DA_TRILHA,
  ROTULO_DO_GESTO,
  type GestoHumano,
} from './dadosDaCaptacao';

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {titulo}
      </p>
      {children}
    </div>
  );
}

/**
 * O botão diz que TIPO de ato ele é, e não só o que faz.
 *
 * "Aprova o envio" é cheio e leva ícone de envio porque é saída externa, e
 * nenhuma sai sem clique de gente — é o ato mais caro da tela e não pode
 * parecer um link. "Automático" é fantasma: o robô já fez, e clicar só
 * reconhece. Entre os dois, conferir e decidir ficam contornados.
 */
const GESTO: Record<
  GestoHumano,
  { variante: 'contained' | 'outlined' | 'text'; Icone: typeof Check }
> = {
  aprova_envio: { variante: 'contained', Icone: Send },
  confirma: { variante: 'outlined', Icone: CheckCheck },
  decide: { variante: 'outlined', Icone: Scale },
  automatico: { variante: 'text', Icone: Eye },
};

/**
 * Um campo do catálogo. O caso diz QUAIS chaves faltam; `CAMPOS` diz qual
 * controle cada chave usa. Não existe "um formulário por caso" — existe um
 * renderizador e uma lista de chaves.
 */
function Campo({
  chave,
  valores,
  set,
}: {
  chave: string;
  valores: Record<string, string>;
  set: (k: string, v: string) => void;
}): React.JSX.Element | null {
  const def = CAMPOS[chave];
  if (!def) return null;
  const v = valores[chave] ?? '';
  return (
    <div className="text-sm">
      <span className="text-muted-foreground">
        {def.rotulo}
        {def.dica ? ` · ${def.dica}` : ''}
      </span>

      {def.tipo === 'select' && (
        <div className="mt-0.5 space-y-1">
          {def.opcoes?.map((o) => (
            <label key={o} className="flex items-start gap-2">
              <input
                type="radio"
                name={chave}
                className="mt-1"
                checked={v === o}
                onChange={() => { set(chave, o); }}
              />
              {o}
            </label>
          ))}
        </div>
      )}

      {def.tipo === 'checklist' && (
        <div className="mt-0.5 space-y-1">
          {def.itens?.map((i) => (
            <label key={i} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={(valores[`${chave}:${i}`] ?? '') === 'ok'}
                onChange={(ev) => { set(`${chave}:${i}`, ev.target.checked ? 'ok' : ''); }}
              />
              {i}
            </label>
          ))}
        </div>
      )}

      {def.tipo === 'arquivo' && (
        <input
          type="file"
          className="mt-0.5 block w-full text-sm file:mr-3 file:rounded file:border file:border-[var(--fio)] file:bg-transparent file:px-2 file:py-1 file:text-sm"
          onChange={(ev) => { set(chave, ev.target.files?.[0]?.name ?? ''); }}
        />
      )}

      {def.tipo === 'texto_longo' && (
        <textarea
          rows={2}
          className="mt-0.5 w-full rounded border border-[var(--fio)] bg-transparent px-2 py-1 text-sm"
          value={v}
          onChange={(ev) => { set(chave, ev.target.value); }}
        />
      )}

      {(def.tipo === 'texto' || def.tipo === 'tel' || def.tipo === 'data' || def.tipo === 'numero') && (
        <input
          type={
            def.tipo === 'tel'
              ? 'tel'
              : def.tipo === 'data'
                ? 'date'
                : def.tipo === 'numero'
                  ? 'number'
                  : 'text'
          }
          className="mt-0.5 w-full rounded border border-[var(--fio)] bg-transparent px-2 py-1 text-sm"
          value={v}
          onChange={(ev) => { set(chave, ev.target.value); }}
        />
      )}
    </div>
  );
}

export function CasoDialog({
  card,
  onOpenChange,
  onResolver,
}: {
  card: AneryCard | null;
  onOpenChange: (aberto: boolean) => void;
  /** A decisão da pessoa: o caso sai daqui autorizado ou declinado. */
  onResolver?: (
    titulo: string,
    desfecho: 'autorizar' | 'declinar',
    motivo?: string,
    trilha?: Trilha,
  ) => void;
}) {
  const navigate = useNavigate();
  const [valores, setValores] = useState<Record<string, string>>({});
  /**
   * O "✓ feito" do último gesto, junto com o gesto que o produziu.
   *
   * Guardar só o texto deixava a confirmação pendurada: o caso saía de
   * "Novo", entrava na apuração, e o rodapé seguia anunciando "Caso aberto".
   * Comparando com o gesto disponível agora, ela some sozinha quando deixa de
   * ser verdade — que é o instante em que há outra coisa a fazer.
   */
  const [registrado, setRegistrado] = useState<{ gesto: string; texto: string } | null>(null);
  // Declinar exige motivo: o POP manda declinar no IW seguindo a Tabela de
  // justificativa de cancelamento (Anexo 4). Declínio sem código de motivo não
  // é declínio, é caso sumido.
  const [declinando, setDeclinando] = useState(false);
  /**
   * A frente aberta por padrão é a que TRAVA — é nela que há trabalho agora.
   * `'nenhuma'` existe para o clique conseguir FECHAR a que abriu sozinha:
   * com `null` valendo "use a trava", fechá-la só a reabria.
   */
  const [aberta, setAberta] = useState<Trilha | 'nenhuma' | null>(null);


  if (!card?.detalhe) return null;
  const d = card.detalhe;
  const m = d.momento ? MOMENTO_ESTADO[d.momento.tipo] : null;
  const e = d.entrada;
  const DADO = ['texto', 'tel', 'data', 'numero', 'arquivo'];
  const faltantes = (e?.campos ?? []).filter((c) => CAMPOS[c] && DADO.includes(CAMPOS[c].tipo));
  const set = (k: string, v: string) => { setValores((p) => ({ ...p, [k]: v })); };
  const trilhas = card.trilhas;
  const foco = aberta === 'nenhuma' ? null : (aberta ?? card.trava?.trilha ?? null);
  const confirmado = registrado && registrado.gesto === e?.botao ? registrado.texto : null;

  return (
    <Dialog open onClose={() => { onOpenChange(false); }} maxWidth="sm" fullWidth>
      {/* `anery-marca` carrega `min-height: 100dvh`, feito para a página. */}
      <div className="anery-marca" style={variaveisDaMarca}>
        <DialogTitle component="div" className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {card.operadora && <Operadora nome={card.operadora} />}
            {m && <Tag label={m.rotulo} tone={m.tone} />}
          </div>
          <p className="text-left text-base font-semibold">
            {card.titulo} · {d.modalidade}
          </p>
          <p className="font-mono text-xs text-tinta-2">
            {d.canal}
            {d.solicitacao ? ` · ${d.solicitacao}` : ''}
          </p>
        </DialogTitle>

        <DialogContent dividers className="space-y-4">
          {(d.dono ?? d.pad ?? d.sla) && (
            /* Ficha do caso: três respostas curtas, em grade de rótulo e
               valor. Antes eram parágrafos com o rótulo embutido no texto —
               "Dono: Ana · Enf. de Captação" —, e nada alinhava, então achar
               o prazo exigia ler a linha inteira. */
            <dl className="anery-ficha-caso">
              {d.dono && (
                <>
                  <dt>Dono</dt>
                  <dd>
                    {d.dono.nome} · {d.dono.papel}
                    {d.dono.nucleo ? ` · núcleo ${d.dono.nucleo}` : ''}
                  </dd>
                </>
              )}
              {d.pad && (
                <>
                  <dt>PAD</dt>
                  <dd>{d.pad}</dd>
                </>
              )}
              {d.sla && (
                <>
                  <dt>Prazo</dt>
                  <dd className="anery-ficha-prazo">
                    <span className="num">
                      {d.sla.chegou} → {d.sla.prazo}
                    </span>
                    <Tag label={d.sla.restante} tone={d.sla.tone} />
                  </dd>
                </>
              )}
            </dl>
          )}

          {d.carimbos && d.carimbos.length > 0 && (
            <Secao titulo="O que já aconteceu">
              {/* Trilho à esquerda e o último marco em destaque: numa lista
                  chapada, "agora" tinha o mesmo peso de um carimbo de três
                  dias atrás, e é o último que diz onde o caso está. */}
              <ol className="anery-trilho">
                {d.carimbos.map((c, i) => (
                  <li
                    key={c.rotulo}
                    className="anery-marco"
                    data-ultimo={i === (d.carimbos?.length ?? 0) - 1 ? '' : undefined}
                  >
                    <span className="anery-marco-rot">{c.rotulo}</span>
                    <span className="num anery-marco-quando">{c.quando}</span>
                  </li>
                ))}
              </ol>
            </Secao>
          )}

          {d.orcamento && (
            <Secao titulo="O que o caso consome">
              <ul className="space-y-1 text-sm">
                {d.orcamento.recursos.map((r) => (
                  <li key={r.item} className="flex flex-wrap justify-between gap-x-4">
                    <span>
                      <span className="cond uppercase text-[0.6875rem] tracking-wider text-tinta-3">{r.grupo}</span>{' '}
                      {r.item}
                    </span>
                    <span className="num text-xs text-tinta-2">{r.qtd}</span>
                  </li>
                ))}
              </ul>
            </Secao>
          )}

          {d.orcamento && (
            <Secao titulo="Equipe e frequência">
              <ul className="space-y-1 text-sm">
                {d.orcamento.equipe.map((e) => (
                  <li key={e.categoria} className="flex flex-wrap justify-between gap-x-4">
                    <span>{e.categoria}</span>
                    <span className="text-xs text-tinta-2">
                      {e.frequencia} · {e.origem}
                    </span>
                  </li>
                ))}
              </ul>
            </Secao>
          )}

          {/* A margem é o que decide se o caso segue, escala ou morre — por isso
              vem com a alçada escrita ao lado, e não só o número. */}
          {d.orcamento && (
            <Secao titulo="Margem e alçada">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="num text-[1.375rem]">{d.orcamento.margem.percentual}%</span>
                <Tag label={d.orcamento.margem.alcada} tone={d.orcamento.margem.tone} />
              </div>
            </Secao>
          )}

          {/* "Falta" é dado ausente. Decisão e conferência não faltam — elas
              esperam alguém, e isso o bloco de baixo já diz. */}
          {faltantes.length > 0 && (
            <Secao titulo="Falta">
              <div className="flex flex-wrap gap-1.5">
                {faltantes.map((c) => (
                  <Tag key={c} label={CAMPOS[c]?.rotulo ?? c} tone="warn" />
                ))}
              </div>
            </Secao>
          )}

          {/* As cinco frentes, com ação em CADA uma.

              O formulário de baixo age na frente cobrada — a que está parada
              há mais tempo. Mas quem abre o caso pode ter acabado de resolver
              outra, e obrigar a seguir a ordem da tela seria reinventar a fila
              que este desenho desfez. */}
          {trilhas && (
            <Secao titulo="As cinco frentes">
              <div className="anery-frentes">
                {ORDEM_DAS_TRILHAS.map((t) => {
                  const tr = trilhas[t];
                  const fechada = tr.feitos >= tr.total;
                  const rotulo = ROTULO_DA_TRILHA[t];
                  const acionavel = !fechada && !tr.esperando && Boolean(tr.botao);
                  // Esperar não é beco: a frente que destrava é clicável, e o
                  // clique abre justamente ela.
                  const leva = tr.esperaDe;
                  const abertaAqui = foco === t;
                  const g = GESTO[tr.gesto ?? 'confirma'];
                  return (
                    <div
                      key={t}
                      className="anery-frente"
                      data-estado={
                        fechada ? 'fechada' : tr.esperando ? 'espera' : tr.tom
                      }
                      data-aberta={abertaAqui ? '' : undefined}
                    >
                      {/* O cabeçalho inteiro é o gatilho: cinco frentes abertas
                          de uma vez viram um formulário de rolar, e o que
                          precisa de atenção some no meio. */}
                      <button
                        type="button"
                        className="anery-frente-topo"
                        aria-expanded={acionavel ? abertaAqui : undefined}
                        onClick={() => {
                          if (leva) { setAberta(leva); return; }
                          setAberta(abertaAqui ? 'nenhuma' : t);
                        }}
                      >
                        <PontoDaTrilha estado={tr} />
                        <span className="anery-frente-nome">{rotulo}</span>
                        <BarraDaTrilha estado={tr} nome={rotulo} />
                        <span className="num anery-frente-conta">
                          {tr.feitos}/{tr.total}
                        </span>
                        <span className="num anery-frente-relogio">
                          {fechada ? 'Concluída' : (tr.idade ? `há ${tr.idade}` : '')}
                        </span>
                        <span className="anery-frente-falta">
                          {fechada ? (
                            'Nada pendente'
                          ) : leva ? (
                            <>
                              {tr.situacao} — preencha em <b>{ROTULO_DA_TRILHA[leva]}</b>
                            </>
                          ) : (
                            <>
                              <b>{tr.situacao}</b>
                              {tr.responsavel && <> · {tr.responsavel}</>}
                            </>
                          )}
                        </span>
                        {acionavel && (
                          <span className="cond anery-frente-gesto">
                            {ROTULO_DO_GESTO[tr.gesto ?? 'confirma']}
                          </span>
                        )}
                        {/* A seta é a única coisa que diz que a linha abre.
                            `cursor: pointer` não é indicação — é o que se
                            descobre DEPOIS de tentar. */}
                        {acionavel ? (
                          <ChevronDown className="anery-frente-seta" aria-hidden="true" />
                        ) : leva ? (
                          <ArrowRight className="anery-frente-seta" aria-hidden="true" />
                        ) : null}
                      </button>

                      {/* Frente que espera outra NÃO ganha formulário nem
                          botão: oferecer o gesto a quem não pode dá-lo é o
                          que faz a fila de hoje perder a confiança de quem a
                          usa. */}
                      {abertaAqui && acionavel && (
                        <div className="anery-frente-forma">
                          {tr.acao && <p className="anery-frente-acao">{tr.acao}</p>}

                          {/* Passo sem campo é passo que o catálogo ainda não
                              sabe descrever — e isso aparece, em vez de virar
                              formulário inventado. */}
                          {(tr.campos ?? []).length > 0 ? (
                            <div className="space-y-2">
                              {(tr.campos ?? []).map((chave) => (
                                <Campo key={chave} chave={chave} valores={valores} set={set} />
                              ))}
                            </div>
                          ) : (
                            <p className="anery-frente-semcampo">
                              Nada a preencher: este passo é o ato em si.
                            </p>
                          )}

                          <Button
                            size="small"
                            variant={g.variante}
                            startIcon={<g.Icone className="h-4 w-4" aria-hidden="true" />}
                            onClick={() => {
                              // Sem fechar o diálogo: o card vem do quadro, a
                              // linha desta frente volta atualizada, e dá para
                              // seguir para a próxima sem reabrir o caso.
                              onResolver?.(card.titulo, 'autorizar', undefined, t);
                            }}
                          >
                            {tr.botao}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Secao>
          )}

          {/* O bloco que importa: o que está acontecendo e quem age. */}
          {d.momento && m && (
            <div className="rounded-md border border-[var(--fio)] bg-[var(--chao-2)] p-3">
              <Aviso tone={m.tone}>{d.momento.texto}</Aviso>
              <p className="mt-2 text-sm font-medium">{d.momento.acao}</p>
              {d.momento.responsavel && d.momento.responsavel !== '—' && (
                <p className="mt-1.5 text-sm">
                  <span className="text-muted-foreground">Com quem está: </span>
                  {d.momento.responsavel}
                </p>
              )}
            </div>
          )}

          {/* O que a pessoa preenche para destravar.

              Um formulário só. O caso diz QUAIS chaves faltam; o catálogo diz
              qual controle cada chave usa. Arquivo, data, telefone e decisão
              convivem na mesma lista — não existe "um formulário por caso".

              NÃO aparece em apuração: lá cada frente já traz o próprio gesto,
              e este bloco repetia o botão da frente que trava — com o mesmo
              rótulo, mas no `variant` padrão do MUI, que é `text`. Dois botões
              iguais, um deles sem peso nenhum, para o mesmo ato. */}
          {e && !trilhas && (
            <Secao titulo="Para destravar">
              {confirmado ? (
                <p className="flex items-start gap-1.5 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                  {confirmado}
                </p>
              ) : (
                <div className="space-y-2">
                  {e.campos.map((chave) => (
                    <Campo key={chave} chave={chave} valores={valores} set={set} />
                  ))}

                  <div className="flex flex-wrap gap-2 pt-1">
                    {/* Cheio: é o gesto principal do bloco, e botão de texto
                        não se lê como ação. */}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setRegistrado({ gesto: e.botao, texto: e.feito });
                        onResolver?.(card.titulo, 'autorizar', undefined, card.trava?.trilha);
                      }}
                    >
                      {e.botao}
                    </Button>
                    {e.botaoAlt && !declinando && (
                      <Button variant="outlined" size="small" onClick={() => { setDeclinando(true); }}>
                        {e.botaoAlt}
                      </Button>
                    )}
                  </div>

                  {declinando && (
                    <div className="mt-2 rounded-md border border-[var(--fio)] p-3">
                      <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                        {CAMPOS.motivo_declinio?.rotulo}
                      </p>
                      <div className="space-y-1">
                        {CAMPOS.motivo_declinio?.opcoes?.map((o) => (
                          <label key={o} className="flex items-start gap-2 text-sm">
                            <input
                              type="radio"
                              name="motivo_declinio"
                              className="mt-1"
                              checked={valores.motivo_declinio === o}
                              onChange={() => { set('motivo_declinio', o); }}
                            />
                            {o}
                          </label>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!valores.motivo_declinio}
                          onClick={() => {
                            setRegistrado({
                              gesto: e.botao,
                              texto: `Declinado no IW · ${valores.motivo_declinio}. Operadora e hospital notificados.`,
                            });
                            onResolver?.(card.titulo, 'declinar', valores.motivo_declinio);
                          }}
                        >
                          Confirmar o declínio
                        </Button>
                        <Button size="small" variant="text" onClick={() => { setDeclinando(false); }}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Secao>
          )}
        </DialogContent>

        <DialogActions className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-mono text-[10px] uppercase tracking-wide text-tinta-3">
            protótipo · o estado não persiste
          </span>
          <div className="flex flex-wrap gap-2">
            {card.irPara && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  onOpenChange(false);
                  void navigate(`/${card.irPara ?? ''}`);
                }}
              >
                Abrir a fila
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={() => { onOpenChange(false); }}>
              Voltar
            </Button>
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
}
